import React, { useCallback, useEffect, useState } from 'react';
import { createContext } from '@shared/utils/createContext';
import type { CreateEventMutationFn, CreateEventMutation } from '../routes/CreateWeddingForm/schema.graphql.generated';
import { useCreateEventMutation } from '../routes/CreateWeddingForm/schema.graphql.generated';
import type { ApolloError } from '@apollo/client';
import type { CreateEventPayload0301 } from '@graphql/generated-base';
import { addError } from '@shared/utils/logger';
import { recordCreateWeddingFlowEvent } from '@apps/acquisitionFunnels/createWedding/createWeddingFlowEvents';
import { classifyCreateEventError } from '@apps/acquisitionFunnels/createWedding/utils/classifyCreateEventError';
import { parseNetworkErrorDetails } from '@shared/utils/parseNetworkErrorDetails';

interface EventListenerCallbackExtraParams {}

type EventListenerCallback<T> = (mutation: T, extraParams: EventListenerCallbackExtraParams) => Promise<unknown>;

interface EventStateContext<T, R> {
  createEvent: T;
  createdEventData?: Nullable<R>;
  isEventCreationSettled: boolean;
  createError?: ApolloError;
  addEventCreatedListener: (cb: EventListenerCallback<R>) => Promise<void>;
}

interface PendingEventListeners {
  cb: EventListenerCallback<CreateEventMutation>;
  inProgress: boolean;
}

type WeddingStateContext = EventStateContext<CreateEventMutationFn, CreateEventMutation>;

const [Provider, useEventState] = createContext<WeddingStateContext>({ name: 'WeddingState' });

/**
 * Error-tracker context for a failed create. The couple's names are identifying data and
 * add nothing to the diagnosis, so they are dropped before the payload reaches New Relic.
 */
const toErrorContext = (payload: CreateEventPayload0301 | undefined) => {
  if (!payload) return undefined;
  const { ownerFirstName, ownerLastName, fianceeFirstName, fianceeLastName, ...rest } = payload;
  return rest;
};

const EventStateProvider: React.FC = ({ children }) => {
  const [mutation, { data: createdEventData, called, loading, error }] = useCreateEventMutation();
  const [pendingEventListeners, setPendingEventListeners] = useState<Set<PendingEventListeners>>(new Set());

  useEffect(() => {
    if (!loading && createdEventData) {
      pendingEventListeners.forEach(eventListener => {
        if (!eventListener.inProgress) {
          eventListener.inProgress = true;
          eventListener.cb(createdEventData, {}).finally(() => {
            setPendingEventListeners(prev => {
              prev.delete(eventListener);
              return new Set(prev);
            });
          });
        }
      });
    }
  }, [createdEventData, loading, pendingEventListeners]);

  const createEvent: CreateEventMutationFn = useCallback(
    options => {
      // Report failures on the call's own promise rather than through Apollo's `onError`.
      // On @apollo/client 3.4.x a per-call `onError` passed to the mutate function is
      // ignored (only the hook-level option is read), which left this reporting dead and
      // surfaced failures only as unhandled rejections. Handling the rejection here keeps the
      // payload scoped to this attempt (no shared ref to race on a double submit) and settles
      // the caller's promise once the failure has been recorded. The hook's `error` state is
      // set by Apollo before the rejection, so the TyingTheKnot error UI is unaffected.
      return mutation(options).catch((error: ApolloError) => {
        // Clear stale listeners from previous failed attempts so they don't fire on retry success
        setPendingEventListeners(new Set());

        const errorType = classifyCreateEventError(error);
        const networkDetails = parseNetworkErrorDetails(error);
        const errorContext = toErrorContext(options?.variables?.payload);

        if (errorType === 'rateLimit') {
          addError('TOO_MANY_REQUESTS', errorContext);
        }
        addError(error, errorContext);
        recordCreateWeddingFlowEvent({
          action: 'EventCreateFailed',
          errorMessage: error?.message ?? 'unknown',
          errorType,
          statusCode: networkDetails.statusCode ?? null,
          isTimeout: networkDetails.isTimeout,
          isHtmlResponse: networkDetails.isHtmlResponse,
        });
        return { data: undefined, errors: error.graphQLErrors };
      });
    },
    [mutation],
  );

  const addEventCreatedListener = useCallback(async (cb: EventListenerCallback<CreateEventMutation>) => {
    setPendingEventListeners(prev => {
      prev.add({ cb, inProgress: false });
      return new Set(prev);
    });
  }, []);

  const isEventCreationSettled = called && !error && !loading && pendingEventListeners.size === 0;

  return <Provider value={{ createEvent, addEventCreatedListener, isEventCreationSettled, createdEventData, createError: error }}>{children}</Provider>;
};

export { useEventState, EventStateProvider };
